import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from '../store'
import Widget from './Widget'
import { BaseWidgetProps, WidgetDescription } from '../helpers/widget'
import { useAppCtx } from '../hooks/use-app-ctx'
import { NoPlaceholderValueError, resolveTemplateString } from '../helpers/resolve-template-string'
import { ErrorNeedAuth, SparqlStatus, useSparqlQuery } from '../helpers/use-sparql-query'
import { useAppLoginStore } from '../api/app-login'
import { queryClient } from '../queryClient'
import { usePrevious } from '../hooks/use-previous'
import { OpaqueSparqlResults } from '../helpers/sparqlResultHelpers'
import { freezeInDevMode } from '../utils/dev-mode'
import isEqual from 'lodash/isEqual'
import { PublishedDict, usePubsub } from "../redux/pubsub"
import { toast } from 'sonner'
import { useReportWidgetQueryStatus } from './editor/widget-editor/query-status'

const EMPTY_RESULT = freezeInDevMode({
  head: {
    vars: []
  },
  results: {
    bindings: []
  }
} satisfies OpaqueSparqlResults)

export function WidgetQueryWrapper(props: BaseWidgetProps & {
  query?: string
  definition: WidgetDescription<any, any, any>
}) {
  const dispatch = useDispatch()
  const [ pubsub ] = usePubsub()

  const prevPubsub = usePrevious(pubsub)

  const layoutName = useSelector(s => s.project.layoutName)

  const viewerConfig = useSelector(s => s.currentViewer?.config)
  const strictSparqlErrors = viewerConfig?.strictSparqlErrors ?? false
  const notifySparqlErrors = viewerConfig?.notifySparqlErrors ?? false

  const endpoint = useAppCtx().urlSparql

  const [ resolvedQuery, setResolvedQuery ] = useState<string | null>(null)

  const shouldResolveQuery = props.withinSelectedTab !== false
    && !!props.query
    && hasQueryDefinition(props.definition)
  const shouldQuery = shouldResolveQuery && !!resolvedQuery

  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!shouldResolveQuery) return

    const resolveQuery = () => {
      if (!props.query) return
      try {
        const resolvedQuery = resolveTemplateString(props.query, pubsub)
        const jsResolvedQuery = runJSInQueryUnsafe(resolvedQuery)
        setResolvedQuery(jsResolvedQuery)
      } catch(e) {
        setResolvedQuery(null)
        if (e instanceof NoPlaceholderValueError) return
        console.error(e)
      }
    }

    if (!prevProps
      || (prevProps.repoURL !== props.repoURL)
      || (prevProps.withinSelectedTab !== props.withinSelectedTab)
      || (prevProps.query !== props.query)
      || !isEqual(prevPubsub, pubsub)
    ) {
      resolveQuery()
    }
  })

  const queryResult = useSparqlQuery({
    enabled: shouldQuery,
    query: resolvedQuery ?? "# disabled",
    sparqlEndpoint: endpoint,
    source: props.widgetId
  })

  const dataStatus = toSparqlStatus(shouldResolveQuery, shouldQuery, queryResult)
  useReportWidgetQueryStatus({
    widgetId: props.widgetId,
    status: dataStatus,
    queryResult,
    // a widget type without a query can keep a stale `query` from before its type was changed
    template: hasQueryDefinition(props.definition) ? props.query : undefined,
    resolvedQuery,
    published: pubsub,
    endpoint,
    resolve: resolveQueryForPreview,
  })

  const error = queryResult.error
  useEffect(() => {
    const reauth = async () => {
      const success = await useAppLoginStore.getState().reauth()
      if (success) return await queryClient.invalidateQueries()
      useAppLoginStore.getState().goToLoginPage()
    }

    if (error instanceof ErrorNeedAuth) {
      void reauth()
    }
  }, [ error ])

  useEffect(function notifyAboutSparqlError() {
    if (!error) return
    if (error instanceof ErrorNeedAuth) return
    if (!notifySparqlErrors) return
    // Right now not using sparqlErrorMessage, since ugly default and not configurable in UI (see default-config.ts)
    toast.error(`SPARQL query failed for ${props.definition.name}`)
  }, [ error, notifySparqlErrors, props.definition.name ])

  return (
    <Widget
      {...props}
      strictSparqlErrors={strictSparqlErrors}
      pubsub={pubsub}
      dispatch={dispatch}
      layoutName={layoutName}
      queryResult={queryResult}
      data={queryResult.data ?? EMPTY_RESULT}
      dataStatus={dataStatus}
      sparqlEndpoint={endpoint} // Debt: BBUpload needs this
    />
  )
}

function resolveQueryForPreview(template: string, published: PublishedDict) {
  return runJSInQueryUnsafe(resolveTemplateString(template, published)) || null
}

function toSparqlStatus(
  shouldResolveQuery: boolean,
  shouldQuery: boolean,
  queryResult: {status: "pending" | "success" | "error", fetchStatus: "fetching" | "paused" | "idle"}
): SparqlStatus {
  if (!shouldResolveQuery) return "disabled"
  // unresolved `{{placeholders}}`: waiting, whatever an earlier run left behind
  if (!shouldQuery) return "disabled"
  // Query with unresolved `{{placeholders}}` is also pending, we don't want widgets spin in that case
  if (queryResult.status === "pending" && queryResult.fetchStatus !== "fetching") return "disabled"
  return queryResult.status
}

// DEBT
function runJSInQueryUnsafe(query: string) {
  try {
    if (query == null) return null
    let b = query.includes('<script>')
    if (b) {
      let codes = query.split('<script>')
      let nquery = ''
      for (let n in codes) {
        if (n === '0') {
          nquery = codes[n]
          continue
        }
        let code = codes[n]

        let codeQuery = code.split('</script>')
        let evalcode = codeQuery[0]
        let evalCode2 = ''
        try {
          evalCode2 = new Function(evalcode)() //eslint-disable-line no-new-func
        } catch (e) {
          console.log('cannot execute js code ', evalcode, e)
        }

        if (evalCode2 == null) {
          evalCode2 = ''
        }
        let subQuery2 = codeQuery[1]
        nquery += evalCode2 + subQuery2
      }
      return nquery
    }
  } catch (error) {
    console.error('error parsing js in query ', error, query)
  }

  return query
}

function hasQueryDefinition(definition: WidgetDescription<any, any, any>) {
  const variables = definition.variables
  const queryDefinition = variables && variables.find((variable) => variable.name === 'query')
  return Boolean(queryDefinition)
}
